import type { AuditDependency } from '../schemas/index';
import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  rangeDep,
  type SkippedDependency,
} from './types';

/**
 * Parses a `Cargo.toml` manifest for Rust/Cargo dependencies.
 *
 * Implementation note: we deliberately avoid adding a TOML library so that
 * this package remains dependency-free beyond Zod.  The parser is a
 * line-scanning state machine that handles the common cases found in
 * real-world Cargo.toml files.
 *
 * Cargo version semantics
 * ───────────────────────
 * Cargo treats a bare `1.0.219` as a *caret* range (`^1.0.219`), which
 * technically matches anything `>=1.0.219, <2.0.0`.  We make a pragmatic
 * exception: a **full X.Y.Z** semver (with optional prerelease/build suffix)
 * is treated as an exact pin because the vast majority of Cargo.toml files
 * that write a full X.Y.Z mean that exact version. `=1.2.3` (leading `=`) is
 * exact too (the `=` is stripped).
 *
 * `^`, `~`, `>=`, `<=`, `>`, `<`, comma ranges, `*` and partial versions
 * (`1.0`, `1`) are emitted as ranges for the Worker to resolve.
 *
 * Only non-registry sources are skipped outright: `path = ...`, `git = ...`
 * without a version, `workspace = true`.
 */

/** Full semver with optional prerelease/build — used for exact-pin detection. */
const FULL_SEMVER_RE = /^\d+\.\d+\.\d+([-+].*)?$/;

/** Normalise a raw Cargo version string to a clean semver for the dep record. */
function normaliseVersion(raw: string): string {
  const t = raw.trim();
  if (t.startsWith('=')) return t.slice(1).trim();
  return t;
}

type CargoClassification = { kind: 'exact' } | { kind: 'range' } | { kind: 'skip'; reason: string };

/** Classify a raw Cargo version/specifier string. */
function classifyCargo(raw: string): CargoClassification {
  const t = raw.trim();
  if (t === '') return { kind: 'skip', reason: 'no version specified' };
  if (t === '*') return { kind: 'range' };
  if (/^(workspace|path|git)\b/.test(t)) return { kind: 'skip', reason: 'not a registry version' };
  // Range prefixes
  if (/^[~^<>]/.test(t) || t.includes(',')) return { kind: 'range' };
  // = prefix is exact (strip it)
  if (t.startsWith('=')) {
    const after = t.slice(1).trim();
    if (FULL_SEMVER_RE.test(after)) return { kind: 'exact' };
    return { kind: 'range' };
  }
  // partial versions: exactly 1 or 2 dot-separated numeric groups
  if (/^\d+$/.test(t) || /^\d+\.\d+$/.test(t)) return { kind: 'range' };
  if (FULL_SEMVER_RE.test(t)) return { kind: 'exact' };
  return { kind: 'range' };
}

// ---------------------------------------------------------------------------
// Section header matching
// ---------------------------------------------------------------------------

/** The dependency-table section headers we understand. */
type DepSection =
  | '[dependencies]'
  | '[dev-dependencies]'
  | '[build-dependencies]'
  | 'target-dependencies'
  | 'other';

function classifyHeader(line: string): DepSection | null {
  const trimmed = line.trim();
  if (trimmed === '[dependencies]') return '[dependencies]';
  if (trimmed === '[dev-dependencies]') return '[dev-dependencies]';
  if (trimmed === '[build-dependencies]') return '[build-dependencies]';
  // [target.'cfg(...)'.dependencies] and friends
  if (/^\[target\.[^\]]+\.(?:dev-)?(?:build-)?dependencies\]$/.test(trimmed))
    return 'target-dependencies';
  // Inline sub-table: [dependencies.serde] — the crate name follows the dot
  if (/^\[(?:dev-|build-)?dependencies\.[^\]]+\]$/.test(trimmed)) return null; // handled separately
  // Any other section header — exits a dep block
  if (trimmed.startsWith('[')) return 'other';
  return null; // not a header
}

/** Extract the crate name from an inline sub-table header, e.g. `[dependencies.serde]`. */
function inlineSubTableName(line: string): string | null {
  const m = /^\[(?:dev-|build-)?dependencies\.([^\]]+)\]/.exec(line.trim());
  if (!m) return null;
  const name = m[1];
  return name !== undefined ? name.trim() : null;
}

// ---------------------------------------------------------------------------
// Key/value extraction helpers
// ---------------------------------------------------------------------------

/** Strip inline `# comment` from a TOML value (outside of strings). */
function stripInlineComment(val: string): string {
  let inStr = false;
  for (let i = 0; i < val.length; i++) {
    const ch = val[i] ?? '';
    const prev = i > 0 ? (val[i - 1] ?? '') : '';
    if (ch === '"' && prev !== '\\') inStr = !inStr;
    if (!inStr && ch === '#') return val.slice(0, i);
  }
  return val;
}

/** Extract a quoted string value from a TOML snippet, e.g. `"1.0.0"` → `1.0.0`. */
function extractQuoted(src: string): string | null {
  const m = /"([^"]*)"/.exec(src);
  if (!m) return null;
  const v = m[1];
  return v !== undefined ? v : null;
}

/**
 * Try to parse `name = "version"` or `name = { version = "x.y.z", ... }` from
 * a single line. Returns `[name, rawVersion]` or `null`.
 */
function parseInlineKV(line: string): [string, string] | null {
  const stripped = stripInlineComment(line).trim();

  // Match: identifier = "value"   (simple string assignment)
  const simple = /^([\w-]+)\s*=\s*"([^"]+)"/.exec(stripped);
  if (simple) {
    const name = simple[1];
    const ver = simple[2];
    if (name !== undefined && ver !== undefined) return [name, ver];
    return null;
  }

  const inline = /^([\w-]+)\s*=\s*\{/.exec(stripped);
  if (inline) {
    const crateName = inline[1];
    if (crateName === undefined) return null;

    const ver = /\bversion\s*=\s*"([^"]+)"/.exec(stripped);
    if (ver) {
      const v = ver[1];
      if (v !== undefined) return [crateName, v];
      return null;
    }

    // Check for path/git without version → skip-worthy but still parseable
    if (/\b(?:path|git)\s*=/.test(stripped) && !/\bversion\s*=/.test(stripped)) {
      return [crateName, '']; // empty version signals "no version"
    }

    // workspace = true
    if (/\bworkspace\s*=\s*true/.test(stripped)) {
      return [crateName, 'workspace'];
    }
    return null;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parses a `Cargo.toml` manifest and extracts its dependencies (exact pins and
 * ranges alike).
 *
 * Throws {@link ManifestParseError} only when the content has no recognisable
 * `[dependencies*]` section header **and** no `version = "..."` key-value
 * lines — i.e. it is clearly not a Cargo.toml.
 */
export const parseCargoToml: ManifestParser = (content: string): ParsedManifest => {
  const lines = content.replace(/\r\n/g, '\n').split('\n');

  const dependencies: AuditDependency[] = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  /**
   * Emit one dependency (or skip entry). Deduplication: first occurrence wins.
   */
  function emit(name: string, rawVersion: string, isSkipForced?: string): void {
    if (seen.has(name)) return;
    seen.add(name);

    if (isSkipForced) {
      skipped.push({ name, requirement: rawVersion || null, reason: isSkipForced });
      return;
    }

    const classified = classifyCargo(rawVersion);
    if (classified.kind === 'exact') {
      const version = normaliseVersion(rawVersion);
      const requirement = rawVersion.startsWith('=') ? rawVersion : null;
      dependencies.push(dep('CARGO', name, version, requirement));
    } else if (classified.kind === 'range') {
      dependencies.push(rangeDep('CARGO', name, rawVersion));
    } else {
      skipped.push({ name, requirement: rawVersion || null, reason: classified.reason });
    }
  }

  /** Current parser state. */
  type State =
    | { kind: 'other' }
    | { kind: 'dep-section' }
    | { kind: 'subtable'; name: string; versionLines: string[] };

  let state: State = { kind: 'other' };

  // Track whether we've seen any recognisable dep-table header or version key.
  let sawDepContent = false;

  for (const raw of lines) {
    const line = raw.trim();

    // Skip blank lines and comments
    if (line === '' || line.startsWith('#')) continue;

    // ---------- Section header -----------------------------------------------
    if (line.startsWith('[')) {
      // Flush any pending subtable state
      if (state.kind === 'subtable') {
        // Extract version from collected lines
        const versionLine = state.versionLines.find((l) => /^\bversion\s*=/.test(l));
        if (versionLine) {
          const v = extractQuoted(versionLine);
          if (v !== null) {
            sawDepContent = true;
            emit(state.name, v);
          }
        } else {
          // path/git subtable without version
          const pathOrGit = state.versionLines.some((l) => /^\b(?:path|git)\s*=/.test(l));
          if (pathOrGit) {
            emit(state.name, '', 'not a registry version');
          } else if (state.versionLines.some((l) => /^\bworkspace\s*=\s*true/.test(l))) {
            emit(state.name, 'workspace = true', 'not a registry version');
          }
        }
        state = { kind: 'other' };
      }

      // Is this an inline sub-table header?
      const subtableName = inlineSubTableName(line);
      if (subtableName) {
        sawDepContent = true;
        state = { kind: 'subtable', name: subtableName, versionLines: [] };
        continue;
      }

      const section = classifyHeader(line);
      if (section === 'other') {
        state = { kind: 'other' };
      } else if (section !== null) {
        sawDepContent = true;
        state = { kind: 'dep-section' };
      }
      continue;
    }

    // ---------- Subtable body -------------------------------------------------
    if (state.kind === 'subtable') {
      const kv = /^([\w-]+)\s*=\s*(.+)$/.exec(line);
      if (kv) {
        const key = kv[1];
        const valRaw = kv[2];
        if (key !== undefined && valRaw !== undefined) {
          const val = stripInlineComment(valRaw).trim();
          state.versionLines.push(`${key} = ${val}`);
        }
      }
      continue;
    }

    // ---------- Dependency section body ---------------------------------------
    if (state.kind === 'dep-section') {
      // Detect `version = "..."` key at root of dep section — not a crate name line
      if (/^version\s*=/.test(line)) {
        sawDepContent = true;
        continue;
      }

      const kv = parseInlineKV(line);
      if (kv) {
        const [name, rawVersion] = kv;
        sawDepContent = true;

        if (rawVersion === '' || rawVersion === 'workspace') {
          const isPath = /\bpath\s*=/.test(line);
          const isGit = /\bgit\s*=/.test(line);
          if (isPath || isGit) {
            emit(name, rawVersion, 'not a registry version');
          } else if (rawVersion === 'workspace') {
            emit(name, 'workspace = true', 'not a registry version');
          }
        } else {
          emit(name, rawVersion);
        }
      }
    }
  }

  // Flush trailing subtable
  if (state.kind === 'subtable') {
    const versionLine = state.versionLines.find((l) => /^version\s*=/.test(l));
    if (versionLine) {
      const v = extractQuoted(versionLine);
      if (v !== null) {
        sawDepContent = true;
        emit(state.name, v);
      }
    } else {
      const pathOrGit = state.versionLines.some((l) => /^(?:path|git)\s*=/.test(l));
      if (pathOrGit) emit(state.name, '', 'not a registry version');
    }
  }

  if (!sawDepContent) {
    throw new ManifestParseError(
      'Content does not appear to be a Cargo.toml (no dependency sections found)',
    );
  }

  return { system: 'CARGO', dependencies, skipped };
};
